import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useJobStore } from "@/dentlab/src/store/Jobs";
import { usePatientStore } from "@/dentlab/src/store/Patient";
import { useCentralStore } from "@/dentlab/src/store/Central";
import { supabase } from "@/lib/supabase";
import { Logger } from "@/lib/logger/Logger";
import { PatientSearchMultiResult } from "@/dentlab/src/store/Search/types";
import {
	CustomTextFieldShadcn,
	InputDensitySize,
} from "@/dentlab/src/forms/form-input-components/custom-input-components/custom-text-field-shadcn.component";
import { TextDateInputDelayedSubmission } from "@/dentlab/src/components/ui/text-date-input";
import {
	InputWrapper,
	INVALID_CONTROL_CLASS,
} from "@/dentlab/src/components/input-wrapper";
import { Check, UserRound } from "lucide-react";
import { cn } from "@/dentlab/src/lib/util";
import { isAutoGeneratedPatientCode } from "@/dentlab/src/lib/utils/string-matching";

type IdentityField = "first_name" | "last_name" | "code" | "birth_date";

const FIELD_LABELS: Record<IdentityField, string> = {
	first_name: "Vorname",
	last_name: "Nachname",
	code: "Code",
	birth_date: "Geburtsdatum",
};

const SEARCH_DEBOUNCE_MS = 300;

interface PatientFieldAutocompleteProps {
	field: IdentityField;
	value: string;
	required?: boolean;
	densitySize?: InputDensitySize;
	inputHeight?: number;
	validationError?: string | null;
	customHelperText?: string;
}

export const PatientFieldAutocomplete: React.FC<
	PatientFieldAutocompleteProps
> = ({
	field,
	value,
	required,
	densitySize,
	inputHeight,
	validationError,
	customHelperText,
}) => {
	const {
		job,
		quickInsertPatientId,
		setPatientField,
		swapToExistingPatient,
	} = useJobStore((state) => ({
		job: state.job,
		quickInsertPatientId: state.quickInsertPatientId,
		setPatientField: state.setPatientField,
		swapToExistingPatient: state.swapToExistingPatient,
	}));
	const { formFieldsPatient, addFormError, removeFormError } = useJobStore(
		(state) => ({
			formFieldsPatient: state.formFieldsPatient,
			addFormError: state.addFormError,
			removeFormError: state.removeFormError,
		})
	);
	const fetchPatientSearchResultsMulti = usePatientStore(
		(state) => state.fetchPatientSearchResultsMulti
	);
	const getPatient = usePatientStore((state) => state.getPatient);
	const organizationId = useCentralStore((state) => state.organization?.id);

	const dropdownActive =
		!job?.patient_id || quickInsertPatientId === job?.patient_id;

	const containerRef = useRef<HTMLDivElement>(null);
	const dropdownRef = useRef<HTMLDivElement>(null);
	const [isOpen, setIsOpen] = useState(false);
	const [results, setResults] = useState<PatientSearchMultiResult[]>([]);
	const [loading, setLoading] = useState(false);
	const [selectedIndex, setSelectedIndex] = useState<number>(-1);
	const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const searchSeqRef = useRef(0);

	const flatResults = useMemo(() => {
		const clientId = job?.client_id;
		const here = results.filter((r) => r.client_id === clientId);
		const other = results.filter((r) => r.client_id !== clientId);
		return { here, other, all: [...here, ...other] };
	}, [results, job?.client_id]);

	const runSearch = useCallback(async () => {
		const { first_name, last_name, code, birth_date } = formFieldsPatient;
		// Auto-generated codes (trigger placeholders like "S0-77") must NOT be used
		// as a search filter — they don't match anyone else. Only include the code
		// in the query when the user has set a meaningful (non-auto-gen) value.
		const codeForSearch =
			code && !isAutoGeneratedPatientCode(code) ? code : null;
		const hasAny =
			!!first_name?.trim() ||
			!!last_name?.trim() ||
			!!codeForSearch?.trim() ||
			!!birth_date?.trim();
		if (!hasAny || !dropdownActive) {
			setResults([]);
			return;
		}
		const seq = ++searchSeqRef.current;
		setLoading(true);
		const data = await fetchPatientSearchResultsMulti({
			first_name: first_name ?? null,
			last_name: last_name ?? null,
			code: codeForSearch,
			birth_date: birth_date ?? null,
			priority_field: field === "code" && !codeForSearch ? null : field,
			clientId: job?.client_id ?? null,
		});
		if (seq !== searchSeqRef.current) return;
		const filtered = (data ?? []).filter(
			(r) => r.id !== quickInsertPatientId
		);
		setResults(filtered);
		setSelectedIndex(filtered.length > 0 ? 0 : -1);
		setLoading(false);
	}, [
		field,
		formFieldsPatient,
		job?.client_id,
		quickInsertPatientId,
		dropdownActive,
		fetchPatientSearchResultsMulti,
	]);

	useEffect(() => {
		if (!dropdownActive || !isOpen) return;
		if (debounceRef.current) clearTimeout(debounceRef.current);
		debounceRef.current = setTimeout(runSearch, SEARCH_DEBOUNCE_MS);
		return () => {
			if (debounceRef.current) clearTimeout(debounceRef.current);
		};
	}, [
		dropdownActive,
		isOpen,
		formFieldsPatient.first_name,
		formFieldsPatient.last_name,
		formFieldsPatient.code,
		formFieldsPatient.birth_date,
		runSearch,
	]);

	useEffect(() => {
		if (!isOpen || selectedIndex < 0 || !dropdownRef.current) return;
		const el = dropdownRef.current.querySelector(
			`[data-autocomplete-index="${selectedIndex}"]`
		) as HTMLElement | null;
		el?.scrollIntoView({ block: "nearest" });
	}, [selectedIndex, isOpen, results]);

	useEffect(() => {
		if (!isOpen) return;
		const handleClickOutside = (e: MouseEvent) => {
			if (
				containerRef.current &&
				!containerRef.current.contains(e.target as Node)
			) {
				setIsOpen(false);
			}
		};
		document.addEventListener("mousedown", handleClickOutside);
		return () =>
			document.removeEventListener("mousedown", handleClickOutside);
	}, [isOpen]);

	const handleFocus = () => {
		if (dropdownActive) {
			setIsOpen(true);
			runSearch();
		}
	};

	const handleChange = async (v: string) => {
		setPatientField(field, v);
		if (field === "code") {
			removeFormError("patients", "code");
			if (
				v &&
				organizationId &&
				job?.patient_id &&
				job?.client_id !== undefined
			) {
				const { data, error } = await supabase
					.from("patients")
					.select("id")
					.eq("code", v)
					.eq("organization_id", organizationId)
					.eq("client_id", job.client_id ?? "")
					.neq("id", job.patient_id)
					.limit(1);
				if (!error && data && data.length > 0) {
					addFormError(
						"patients",
						"code",
						"Ein Patient mit diesem Code existiert bereits für diesen Kunden."
					);
				}
			}
		}
	};

	const handleSelect = async (picked: PatientSearchMultiResult) => {
		setIsOpen(false);
		const full = await getPatient(picked.id);
		if (!full) {
			Logger.error("Could not fetch selected patient");
			return;
		}
		await swapToExistingPatient(full);
	};

	const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (!dropdownActive) return;
		const total = flatResults.all.length;
		if (e.key === "ArrowDown") {
			e.preventDefault();
			if (!isOpen) setIsOpen(true);
			if (total > 0) {
				setSelectedIndex((i) => (i + 1) % total);
			}
		} else if (e.key === "ArrowUp") {
			e.preventDefault();
			if (total > 0) {
				setSelectedIndex((i) => (i <= 0 ? total - 1 : i - 1));
			}
		} else if (e.key === "Enter") {
			if (
				isOpen &&
				selectedIndex >= 0 &&
				selectedIndex < flatResults.all.length
			) {
				e.preventDefault();
				handleSelect(flatResults.all[selectedIndex]);
			}
		} else if (e.key === "Escape") {
			setIsOpen(false);
		} else if (e.key === "Tab") {
			// Let the browser move focus to the next field; just close the dropdown.
			setIsOpen(false);
		}
	};

	const renderInput = () => {
		const commonProps = {
			onFocus: handleFocus,
			onKeyDown: handleKeyDown,
			"aria-label": `${FIELD_LABELS[field]} – Patient suchen`,
		};
		if (field === "birth_date") {
			return (
				<InputWrapper
					label={FIELD_LABELS[field]}
					required={required}
					validationError={validationError}
					customHelperText={customHelperText}
					className="w-full"
				>
					<TextDateInputDelayedSubmission
						value={value || ""}
						onDateChange={(date) =>
							setPatientField("birth_date", date)
						}
						style={
							inputHeight
								? { height: `${inputHeight}px` }
								: undefined
						}
						// TextDateInputDelayedSubmission has no validationError prop,
						// only className — so the destructive border is applied here.
						className={cn(validationError && INVALID_CONTROL_CLASS)}
						data-testid={`patient-autocomplete-${field}`}
						{...commonProps}
					/>
				</InputWrapper>
			);
		}
		return (
			<CustomTextFieldShadcn
				label={FIELD_LABELS[field]}
				name={field}
				value={value || ""}
				onChange={(e) => handleChange(e.target.value)}
				required={required}
				densitySize={densitySize}
				inputHeight={inputHeight}
				validationError={validationError}
				customHelperText={customHelperText}
				data-testid={`patient-autocomplete-${field}`}
				{...commonProps}
			/>
		);
	};

	return (
		<div ref={containerRef} className="relative w-full">
			{renderInput()}
			{isOpen && dropdownActive && (
				<div
					ref={dropdownRef}
					className="absolute z-50 top-full left-0 right-0 mt-1 bg-popover border rounded-md shadow-md max-h-80 overflow-y-auto"
					data-testid="patient-autocomplete-dropdown"
				>
					{loading && flatResults.all.length === 0 && (
						<div className="px-3 py-2 text-xs text-muted-foreground">
							Suche…
						</div>
					)}
					{!loading && flatResults.all.length === 0 && value && (
						<div className="px-3 py-2 text-xs text-muted-foreground">
							Keine Ergebnisse gefunden
						</div>
					)}
					{flatResults.here.length > 0 && (
						<AutocompleteGroup
							label="Patienten dieses Kunden"
							items={flatResults.here}
							offset={0}
							selectedIndex={selectedIndex}
							onSelect={handleSelect}
							showClientName={false}
						/>
					)}
					{flatResults.other.length > 0 && (
						<AutocompleteGroup
							label="Andere Patienten"
							items={flatResults.other}
							offset={flatResults.here.length}
							selectedIndex={selectedIndex}
							onSelect={handleSelect}
							showClientName={true}
						/>
					)}
				</div>
			)}
		</div>
	);
};

interface AutocompleteGroupProps {
	label: string;
	items: PatientSearchMultiResult[];
	offset: number;
	selectedIndex: number;
	onSelect: (picked: PatientSearchMultiResult) => void;
	showClientName: boolean;
}

const AutocompleteGroup: React.FC<AutocompleteGroupProps> = ({
	label,
	items,
	offset,
	selectedIndex,
	onSelect,
	showClientName,
}) => (
	<div>
		<div className="px-3 py-1 text-[10px] font-medium text-muted-foreground uppercase tracking-wide bg-muted/30">
			{label}
		</div>
		{items.map((item, i) => {
			const absoluteIndex = offset + i;
			const selected = absoluteIndex === selectedIndex;
			const fullName = [item.first_name, item.last_name]
				.filter(Boolean)
				.join(" ");
			return (
				<button
					type="button"
					key={item.id}
					tabIndex={-1}
					data-autocomplete-index={absoluteIndex}
					onMouseDown={(e) => {
						e.preventDefault();
						onSelect(item);
					}}
					className={cn(
						"w-full px-3 py-2 text-left text-sm hover:bg-accent flex items-center gap-2",
						selected && "bg-accent"
					)}
					data-testid={`patient-autocomplete-item-${item.id}`}
				>
					<UserRound className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
					<div className="flex-1 min-w-0">
						<div className="truncate">
							{fullName || (
								<span className="text-muted-foreground italic">
									(ohne Name)
								</span>
							)}
						</div>
						<div className="text-xs text-muted-foreground truncate">
							{item.code && <span>{item.code}</span>}
							{item.birth_date && (
								<span>
									{item.code ? " · " : ""}
									{item.birth_date}
								</span>
							)}
							{showClientName && item.client_name && (
								<span>
									{item.code || item.birth_date ? " · " : ""}
									Kunde: {item.client_name}
								</span>
							)}
						</div>
					</div>
					{selected && <Check className="w-3.5 h-3.5" />}
				</button>
			);
		})}
	</div>
);
